import React from "react";
import logoUrl from "../../assets/logo.svg";
import {
	Button,
	makeStyles,
	tokens,
	Text,
	Spinner,
	Avatar,
	Menu,
	MenuTrigger,
	MenuPopover,
	MenuList,
	MenuItem,
} from "@fluentui/react-components";
import {
	SignOut24Regular,
	Person24Regular,
	Settings24Regular,
} from "@fluentui/react-icons";
import { useAuth } from "../context/BaseAuthProvider";
import { useSubscription } from "../context/SubscriptionContext";
import { useEditor } from "../editor/EditorContext";
import { featureFlags } from "@cuntas-mitool/shared";

const useStyles = makeStyles({
	header: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: tokens.spacingVerticalM,
		borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
		// marginBottom: tokens.spacingVerticalXS,
		minHeight: "58px",
		boxSizing: "border-box",
	},
	headerBrand: {
		display: "flex",
		alignItems: "center",
		gap: tokens.spacingHorizontalS,
	},
	headerLogo: {
		width: "24px",
		height: "24px",
	},
	headerTitle: {
		fontWeight: tokens.fontWeightSemibold,
		fontSize: tokens.fontSizeBase400,
		color: tokens.colorPaletteGreenForeground1,
	},
	userSection: {
		display: "flex",
		alignItems: "center",
		gap: tokens.spacingHorizontalS,
	},
	memberPill: {
		display: "inline-flex",
		alignItems: "center",
		gap: tokens.spacingHorizontalXXS,
		backgroundColor: tokens.colorPaletteGreenBackground1,
		color: tokens.colorPaletteGreenForeground1,
		fontWeight: tokens.fontWeightSemibold,
		fontSize: tokens.fontSizeBase200,
		paddingTop: tokens.spacingVerticalXXS,
		paddingBottom: tokens.spacingVerticalXXS,
		paddingLeft: tokens.spacingHorizontalS,
		paddingRight: tokens.spacingHorizontalS,
		borderRadius: tokens.borderRadiusCircular,
	},
});

interface HeaderProps {
	onAccountSettings: () => void;
}

function Header({ onAccountSettings }: HeaderProps) {
	const styles = useStyles();
	const {
		user,
		isLoading,
		isAuthenticated,
		login,
		logout,
	} = useAuth();
	const { subscriptionStatus, isTrialActive } =
		useSubscription();
	const isGoogleDocs = useEditor().host === "gdocs";

	return (
		<div className={styles.header}>
			<div className={styles.headerBrand}>
				<img
					src={logoUrl}
					className={styles.headerLogo}
					alt="Cuntas MíTool logo"
				/>
				<Text className={styles.headerTitle}>
					Cuntas MíTool
				</Text>
			</div>
			<div className={styles.userSection}>
				{isLoading ? (
					<Spinner size="tiny" />
				) : isAuthenticated && user ? (
					<Menu>
						<MenuTrigger disableButtonEnhancement>
							<Button
								appearance="subtle"
								icon={<Avatar name={user.name} size={24} />}
								style={{
									textAlign: "left",
									gap: "0.25rem",
								}}
							>
								{user.name}
							</Button>
						</MenuTrigger>
						<MenuPopover>
							<MenuList>
								<MenuItem disabled>
									<Text size={200}>{user.email}</Text>
									{subscriptionStatus === "active" &&
										!isTrialActive && (
											<span className={styles.memberPill}>
												Member
											</span>
										)}
								</MenuItem>
								<MenuItem
									icon={<Settings24Regular />}
									onClick={onAccountSettings}
								>
									Account Settings
								</MenuItem>
								<MenuItem
									icon={<SignOut24Regular />}
									onClick={logout}
								>
									Sign out
								</MenuItem>
							</MenuList>
						</MenuPopover>
					</Menu>
				) : (
					<Menu>
						<MenuTrigger disableButtonEnhancement>
							<Button
								appearance="primary"
								icon={<Person24Regular />}
							>
								Sign in
							</Button>
						</MenuTrigger>
						<MenuPopover>
							<MenuList>
								{!isGoogleDocs && (
									<MenuItem
										onClick={() => login("microsoft")}
									>
										Sign in with Microsoft
									</MenuItem>
								)}
								{(isGoogleDocs || featureFlags.googleAuth) && (
									<MenuItem onClick={() => login("google")}>
										Sign in with Google
									</MenuItem>
								)}
							</MenuList>
						</MenuPopover>
					</Menu>
				)}
			</div>
		</div>
	);
}

export default Header;
